import type { ReactNode } from "react";
import { CircleCheck, CircleX, Clock, ExternalLink, type LucideIcon } from "lucide-react";

import AppStatusPill from "@/components/layout/AppStatusPill";
import { DecisionStrip } from "@/components/underwriting/DecisionStrip";
import { applicationPath } from "@/components/borrower/openApplication";
import { cn } from "@/lib/utils";
import { APP_STATUSES, type AppStatus } from "@/types/borrower";

import { isDeclineOption } from "../../choices/wire";
import type { WireChoice } from "../../protocol/protocol";
import { AssistantCard, AssistantCardHeader } from "../../theme";
import { stripProps, type AnalysisStrip, type TermChange } from "./payloads";

/** As wide as the thread: the strip, the Offer Builder and a decision column side by side. */
const DECISION_CARD = "w-full max-sm:-mx-3 max-sm:w-auto";

/** What a read-only card says in place of editing (it lists no `overrides`). */
export const CHANGE_HINT = "Ask the assistant to change the terms.";

/** What a card whose terms the user may change says. */
export const EDIT_HINT = "Change the terms here, or ask the assistant.";

const isAppStatus = (value: string | null | undefined): value is AppStatus =>
  value != null && (APP_STATUSES as readonly string[]).includes(value);

export function CardShell({
  icon,
  title,
  status,
  children,
  ...props
}: {
  icon: LucideIcon;
  title: string;
  /** The application's status, as the pill at the header's right. */
  status: string | null | undefined;
  children: ReactNode;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <AssistantCard className={DECISION_CARD} {...props}>
      <AssistantCardHeader icon={icon} title={title} className="border-b pb-3">
        {isAppStatus(status) && <AppStatusPill status={status} />}
      </AssistantCardHeader>
      {children}
    </AssistantCard>
  );
}

/** The section label the dashboard's header panes use. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </p>
  );
}

/**
 * What a terms edit changes, quietly under the terms: each changed term's old value struck
 * through beside the new one. `fresh`: a new offer, with nothing before it.
 */
export function TermChanges({ changes, fresh }: { changes: TermChange[]; fresh?: string }) {
  if (!changes.length && !fresh) return null;
  return (
    <div className="mt-3" data-testid="term-changes">
      <SectionLabel>Changes</SectionLabel>
      {fresh ? (
        <p className="text-xs text-muted-foreground">{fresh}</p>
      ) : (
        <ul className="space-y-0.5 text-xs">
          {changes.map(({ label, before, after }) => (
            <li key={label} className="flex flex-wrap items-baseline gap-x-1.5">
              <span className="text-muted-foreground">{label}</span>
              <s className="tabular-nums text-muted-foreground">{before}</s>
              <span className="text-muted-foreground">
                <span aria-hidden>→</span>
                <span className="sr-only">to</span>
              </span>
              <span className="font-medium tabular-nums">{after}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The AI Analysis header's figures; nothing without a finished statements analysis. */
export function StripSection({ strip }: { strip: AnalysisStrip | null }) {
  if (!strip) return null;
  return (
    <div className="border-b px-3.5 py-3">
      <DecisionStrip {...stripProps(strip)} />
    </div>
  );
}

/**
 * The decision column's height in a card (a TermsRow's): the header pane's 7rem only beside the
 * terms, where it centres the buttons on them; stacked under them (a phone, the lip) only its own,
 * or it leaves a gap under the buttons.
 */
export const CARD_DECISION_COLUMN = "min-h-0 [@container_decision-row_(min-width:34rem)]:min-h-28";

/**
 * The terms beside the decision column, as the dashboard's header row: side by side once the
 * card's own width allows (34rem), stacked below that (the lip, a phone).
 */
export function TermsRow({ terms, column }: { terms: ReactNode; column?: ReactNode }) {
  return (
    <div className="border-b [container:decision-row/inline-size]">
      <div
        className={cn(
          "grid",
          column && "[@container_decision-row_(min-width:34rem)]:grid-cols-[minmax(0,1fr)_11rem]"
        )}
      >
        <div className="min-w-0 px-3.5 py-3">{terms}</div>
        {column && (
          <div className="border-t [@container_decision-row_(min-width:34rem)]:border-l [@container_decision-row_(min-width:34rem)]:border-t-0">
            {column}
          </div>
        )}
      </div>
    </div>
  );
}

/** How to change what the card proposes (see CHANGE_HINT). */
export function ChangeHint({ children = CHANGE_HINT }: { children?: ReactNode }) {
  return <p className="mt-2 text-xs text-muted-foreground">{children}</p>;
}

/** Where "View application" goes in place of the app's own page, and how it opens there. */
export interface CardLink {
  /** An absolute URL (an MCP view's frame is not the app's origin). */
  href: string;
  /** Opens it instead of the browser (an MCP host's `ui/open-link`). */
  onOpen: () => void;
}

/**
 * How to change a waiting card, or, once a press redrew it at the user's changes (a
 * "review_changes" refusal), what to do next: the hint's stronger line, not a failure.
 */
export function WaitingHint({ redrawn, hint }: { redrawn: string | null; hint: string }) {
  if (!redrawn) return <ChangeHint>{hint}</ChangeHint>;
  return (
    <p role="status" className="mt-2 text-xs font-medium text-primary">
      {redrawn}
    </p>
  );
}

/** "View application", opened beside the chat. */
function ApplicationLink({ applicationId, link }: { applicationId: number; link?: CardLink }) {
  return (
    <a
      href={link?.href ?? applicationPath(applicationId)}
      target="_blank"
      rel="noreferrer"
      onClick={
        link &&
        ((event) => {
          event.preventDefault();
          link.onOpen();
        })
      }
      className="ml-auto inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline"
    >
      View application
      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </a>
  );
}

export function CardFooter({
  applicationId,
  link,
  children,
}: {
  applicationId: number | null;
  link?: CardLink;
  /** The receipt line, or the card's own action ("Change to approve"). */
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-3.5 py-2.5">
      {children}
      {applicationId != null && <ApplicationLink applicationId={applicationId} link={link} />}
    </div>
  );
}

const VIA_LABELS: Record<string, string> = {
  web: "web",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  mcp: "an app connected to Steddy",
};

const formatTime = (iso: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));

type ReceiptTone = "success" | "declined" | "muted";

const RECEIPT_TONES: Record<ReceiptTone, { icon: LucideIcon; className: string }> = {
  success: { icon: CircleCheck, className: "text-green-700 dark:text-green-400" },
  declined: { icon: CircleX, className: "text-muted-foreground" },
  muted: { icon: Clock, className: "text-muted-foreground" },
};

/**
 * What a decided choice came to, as the approval view words it: "Funded on web · Oct 20, 4:12
 * PM", or what a closed one left ("Expired · nothing changed"). A call that ran without a choice
 * (an auto-decline) is just `done`.
 */
export function ReceiptLine({
  choice,
  done,
  notDone,
  tone: ranTone = "success",
}: {
  choice: WireChoice | null;
  /** The approved call's word ("Funded", "Saved"). */
  done: string;
  /** The turned-down call's ("Not funded", "Not saved"). */
  notDone: string;
  /** The tone of `done` (an auto-decline's is "declined"). */
  tone?: ReceiptTone;
}) {
  const declined =
    choice?.status === "decided" && (!choice.chosen || isDeclineOption(choice.chosen));
  const closed = choice?.status === "expired" || choice?.status === "cancelled";
  const tone: ReceiptTone = closed ? "muted" : declined ? "declined" : ranTone;
  const what = closed
    ? choice?.status === "expired"
      ? "Expired"
      : "Superseded"
    : declined
      ? notDone
      : done;
  const via = choice?.decided_via ? VIA_LABELS[choice.decided_via] : null;
  const parts = [
    via && !closed ? `${what} on ${via}` : what,
    !closed && choice?.decided_at ? formatTime(choice.decided_at) : null,
    closed || declined ? "nothing changed" : null,
  ].filter(Boolean);
  const { icon: Icon, className } = RECEIPT_TONES[tone];
  return (
    <p className={cn("flex min-w-0 items-center gap-1.5 text-xs font-medium", className)}>
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="min-w-0 truncate">{parts.join(" · ")}</span>
    </p>
  );
}
